import Box from "@cloudscape-design/components/box"
import FormField from "@cloudscape-design/components/form-field"
import Link from "@cloudscape-design/components/link"
import Multiselect, { type MultiselectProps } from "@cloudscape-design/components/multiselect"

import { ReadOnlyValue } from "../shared/ReadOnlyValue"
import { MetadataField } from "./useMetadataFields"

/**
 * Props for the FieldMultiSelect component.
 */
interface FieldMultiSelectProps {
  /**
   * Currently selected field names.
   */
  value: string[]

  /**
   * Callback when selection changes.
   */
  onChange: (value: string[]) => void

  /**
   * Available fields from metadata.
   */
  availableFields: MetadataField[]

  /**
   * Whether the input is disabled.
   */
  disabled?: boolean

  /**
   * Placeholder text when no selection.
   */
  placeholder?: string

  /**
   * Whether the field has a validation error.
   */
  hasError?: boolean

  /**
   * When true, render the selected fields as plain text instead of a (disabled) multiselect (#709).
   */
  readOnly?: boolean

  /**
   * Callback to navigate to the Metadata tab.
   */
  onNavigateToMetadata?: () => void
}

/**
 * Multi-select component for choosing multiple fields from metadata. Displays selected fields as
 * removable tokens with a dropdown to add more. Shows empty state with link to Metadata tab when no
 * fields available. Fields selected but no longer present in metadata are flagged as orphaned.
 */
export const FieldMultiSelect: React.FC<FieldMultiSelectProps> = ({
  value,
  onChange,
  availableFields,
  disabled,
  placeholder = "Add field",
  hasError,
  readOnly,
  onNavigateToMetadata,
}) => {
  if (readOnly) {
    return <ReadOnlyValue value={value.join(", ")} />
  }

  const isEmpty = availableFields.length === 0

  if (isEmpty) {
    return (
      <FormField
        constraintText={
          <Box color="text-status-warning" fontSize="body-s">
            No fields defined.{" "}
            {onNavigateToMetadata && (
              <Link variant="primary" onFollow={onNavigateToMetadata}>
                Add fields in the Metadata tab →
              </Link>
            )}
          </Box>
        }
      >
        <Multiselect
          disabled
          selectedOptions={[]}
          onChange={() => {}}
          options={[]}
          placeholder="No fields available"
        />
      </FormField>
    )
  }

  const availableFieldNames = new Set(availableFields.map((f) => f.name))
  const orphanedFields = value.filter((v) => !availableFieldNames.has(v))

  const options: MultiselectProps.Option[] = availableFields.map((field) => ({
    value: field.name,
    label: field.name,
    description: field.label,
  }))

  const selectedOptions: MultiselectProps.Option[] = value.map((v) => {
    const field = availableFields.find((f) => f.name === v)
    return field
      ? { value: v, label: v, description: field.label }
      : { value: v, label: `${v} (not in metadata)` }
  })

  const orphanedError =
    orphanedFields.length === 1
      ? `Field "${orphanedFields[0]}" not found in Metadata`
      : orphanedFields.length > 1
        ? `Fields not found in Metadata: ${orphanedFields.join(", ")}`
        : undefined

  const multiselect = (
    <Multiselect
      selectedOptions={selectedOptions}
      onChange={({ detail }) =>
        onChange(detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v))
      }
      options={options}
      disabled={disabled}
      placeholder={placeholder}
      invalid={hasError || orphanedFields.length > 0}
      filteringType="auto"
      deselectAriaLabel={(option) => `Remove ${option?.value ?? ""}`}
    />
  )

  // Only wrap in a FormField when there are orphaned fields to surface; otherwise let the
  // caller's FormField own the label/description so FormFields don't nest.
  if (orphanedError) {
    return <FormField errorText={orphanedError}>{multiselect}</FormField>
  }

  return multiselect
}
